// ============================================================================
// ProductionControlRoom_SharedUI.tsx
// Primitives UI partagées entre les panneaux de la régie
//
// ⚠ Tout ce qui concerne SDP a été déplacé dans SdpDebugPanel.tsx.
//   Importer depuis là-bas :
//     import { colorizeSdpLine, getSdpLineColor, SdpAccordion,
//              SdpDebugButton, SdpDebugPanel } from './SdpDebugPanel';
//
// ⚠ DeckButton et DECK_COLORS ont été déplacés dans ProductionControlRoom_Deck.tsx
//   car ils sont spécifiques au deck.
// ============================================================================

import React from 'react';

// ─── PanelOverlay ─────────────────────────────────────────────────────────────

interface PanelOverlayProps {
  onClose:  () => void;
  children: React.ReactNode;
  zIndex?:  number;
}

export const PanelOverlay: React.FC<PanelOverlayProps> = ({ onClose, children, zIndex = 200 }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
  >
    {children}
  </div>
);

// ─── PanelHeader ──────────────────────────────────────────────────────────────

interface PanelHeaderProps {
  title:     string;
  subtitle?: string;
  icon?:     string;
  onClose:   () => void;
  actions?:  React.ReactNode;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({ title, subtitle, icon, onClose, actions }) => (
  <div style={{
    padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0,
  }}>
    <div>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#f0f6fc' }}>
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}{title}
      </h2>
      {subtitle && (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
          {subtitle}
        </p>
      )}
    </div>
    <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 16, alignItems: 'center' }}>
      {actions}
      <button
        onClick={onClose}
        style={{
          background: 'rgba(255,255,255,0.06)', border: 'none',
          color: 'rgba(255,255,255,0.6)', width: 30, height: 30,
          borderRadius: '50%', cursor: 'pointer', fontSize: 15, flexShrink: 0,
        }}
      >×</button>
    </div>
  </div>
);

// ─── InfoRow ──────────────────────────────────────────────────────────────────

interface InfoRowProps {
  label:  string;
  value?: string | React.ReactNode;
  accent?: 'green' | 'blue' | 'orange' | 'red' | 'purple';
}

const ACCENT_COLORS: Record<NonNullable<InfoRowProps['accent']>, string> = {
  green:  '#4ade80',
  blue:   '#60a5fa',
  orange: '#fb923c',
  red:    '#f87171',
  purple: '#c084fc',
};

export const InfoRow: React.FC<InfoRowProps> = ({ label, value, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', gap: 8 }}>
    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{label}</span>
    <span style={{
      fontSize: 12, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word',
      color: accent ? ACCENT_COLORS[accent] : 'rgba(255,255,255,0.85)',
    }}>
      {value ?? '—'}
    </span>
  </div>
);

// ─── Section ──────────────────────────────────────────────────────────────────

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.04)', borderRadius: 12,
    padding: '12px 14px', border: '1px solid rgba(255,255,255,0.06)',
  }}>
    <p style={{
      margin: '0 0 8px', fontSize: 11, fontWeight: 700,
      color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.8px',
    }}>
      {title}
    </p>
    {children}
  </div>
);

// ─── StatusDot ────────────────────────────────────────────────────────────────

const STATUS_COLOR_MAP: Record<string, string> = {
  connected:    '#4ade80',
  connecting:   '#fbbf24',
  disconnected: '#64748b',
  failed:       '#f87171',
  new:          '#64748b',
  closed:       '#64748b',
};

const resolveStatusColor = (state?: string): string =>
  (state && STATUS_COLOR_MAP[state]) ?? '#64748b';

interface StatusDotProps {
  state?: string;
  label?: string;
}

export const StatusDot: React.FC<StatusDotProps> = ({ state, label }) => {
  const color = resolveStatusColor(state);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: color,
        boxShadow: state === 'connected' ? `0 0 6px ${color}` : 'none',
        display: 'inline-block', flexShrink: 0,
      }} />
      {label && <span style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</span>}
    </span>
  );
};

// ─── InfoEmpty ────────────────────────────────────────────────────────────────

export const InfoEmpty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
    {children}
  </p>
);

// ─── CodecBadge ───────────────────────────────────────────────────────────────

type CodecBadgeHighlight = 'green' | 'blue' | 'orange' | 'gray';

const CODEC_BADGE_STYLES: Record<CodecBadgeHighlight, React.CSSProperties> = {
  green:  { background: 'rgba(74,222,128,0.1)',   border: '1px solid rgba(74,222,128,0.25)',  color: '#4ade80' },
  blue:   { background: 'rgba(96,165,250,0.1)',   border: '1px solid rgba(96,165,250,0.25)',  color: '#60a5fa' },
  orange: { background: 'rgba(251,191,36,0.1)',   border: '1px solid rgba(251,191,36,0.25)',  color: '#fbbf24' },
  gray:   { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',  color: 'rgba(255,255,255,0.4)' },
};

export const CodecBadge: React.FC<{ label: string; highlight: CodecBadgeHighlight }> = ({ label, highlight }) => (
  <span style={{
    fontSize: 10, fontWeight: 700, padding: '3px 8px',
    borderRadius: 6, fontFamily: 'monospace',
    ...CODEC_BADGE_STYLES[highlight],
  }}>
    {label}
  </span>
);

// ─── DeviceStreamPreview ──────────────────────────────────────────────────────

export const DeviceStreamPreview: React.FC<{ stream?: MediaStream | null }> = ({ stream }) => {
  const ref = React.useRef<HTMLVideoElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.srcObject = stream ?? null;
  }, [stream]);
  return (
    <video
      ref={ref} autoPlay playsInline muted
      style={{ width: '100%', borderRadius: 8, background: '#000', aspectRatio: '16/9', display: 'block' }}
    />
  );
};

// ─── VuMeter ──────────────────────────────────────────────────────────────────

interface VuMeterProps {
  active: boolean;
  label:  string;
  color:  'red' | 'green';
}

const VU_COLOR_MAP = {
  red: {
    gradient:   'linear-gradient(to top, #ef4444, #fca5a5)',
    border:     'rgba(239,68,68,0.5)',
    bg:         'rgba(0,0,0,0.55)',
    labelColor: '#fca5a5',
  },
  green: {
    gradient:   'linear-gradient(to top, #22c55e, #86efac)',
    border:     'rgba(34,197,94,0.4)',
    bg:         'rgba(34,197,94,0.12)',
    labelColor: '#86efac',
  },
};

const VU_BAR_DEFS = [
  { dur: '0.40s', delay: '0.00s', minH: '20%', maxH: '70%'  },
  { dur: '0.30s', delay: '0.05s', minH: '30%', maxH: '90%'  },
  { dur: '0.50s', delay: '0.02s', minH: '15%', maxH: '80%'  },
  { dur: '0.35s', delay: '0.08s', minH: '40%', maxH: '100%' },
  { dur: '0.45s', delay: '0.04s', minH: '20%', maxH: '75%'  },
  { dur: '0.38s', delay: '0.07s', minH: '25%', maxH: '85%'  },
  { dur: '0.42s', delay: '0.01s', minH: '10%', maxH: '60%'  },
];

export const VuMeter: React.FC<VuMeterProps> = ({ active, label, color }) => {
  if (!active) return null;

  const c          = VU_COLOR_MAP[color];
  const animPrefix = `vuMeter_${color}`;
  const keyframes  = VU_BAR_DEFS.map((b, i) =>
    `@keyframes ${animPrefix}_${i} { 0%,100%{height:${b.minH}} 50%{height:${b.maxH}} }`
  ).join('\n');

  return (
    <>
      <style>{keyframes}</style>
      <div style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 5,
        padding: '8px 10px 6px', background: c.bg, backdropFilter: 'blur(6px)',
        borderRadius: 10, border: `1px solid ${c.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 32 }}>
          {VU_BAR_DEFS.map((b, i) => (
            <div key={i} style={{
              width: 5, minHeight: 3, borderRadius: '2px 2px 0 0',
              background: c.gradient,
              animation: `${animPrefix}_${i} ${b.dur} ease-in-out infinite`,
              animationDelay: b.delay,
            }} />
          ))}
        </div>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '1.5px',
          color: c.labelColor, textTransform: 'uppercase',
        }}>
          {label}
        </span>
      </div>
    </>
  );
};
